/**
 * Imagery view preferences (selected date, style, cloud threshold, range).
 *
 * Deliberately NOT part of an overlay preset. A preset is a configuration you
 * keep; a date is where you happen to be looking. Baking 2026-08-12 into a saved
 * preset would pin its owner to that scene forever — so this lives beside the
 * map viewport instead, as per-browser view state.
 */

const KEY = 'goldfinder.imagery.view';

export const DEFAULT_PREFS = {
  date: null,        // null = follow the newest clear scene
  sensor: 's2',      // 's2' (10 m, 2015+) | 'landsat' (30 m, 1986+)
  styleId: 'simple_rgb',
  maxCloud: 20,
  rangeId: '2y',
  // Timeline mode: one date, two dates, or a scan for clearings across a range.
  mode: 'single',    // 'single' | 'compare' | 'clearings'
  compare: false,    // kept in sync with mode for the existing diff layer
  // Clearing scan
  scanDates: 7,      // observations sampled across the range (4-9)
  scanSensor: 's2',  // 's2' (10 m, 2015+) | 'landsat' (30 m, 1986+)
  clearingThreshold: 0.14,
  fromDate: null,    // null = fall back to the oldest clear date in view
  toDate: null,      // null = fall back to the newest clear date in view
  sensitivity: 24,   // luminance threshold; lower = more sensitive
  changesOnly: true, // grey down the imagery so the changes carry the colour
  // Land Change is view state, not a saved configuration: leaving it off on
  // every reload meant re-enabling it each visit, unlike the base map and
  // viewport which are remembered. A preset that carries landChange still
  // wins, because applying a preset overwrites this on load.
  landChangeVisible: null,   // null = fall back to the preset's value
  landChangeOpacity: null,
};

/**
 * @param pickerAvailable  whether the user currently has UI to change sensor
 *                         and date. When the time bar is hidden they do not.
 *
 * A remembered sensor/date is only safe to restore while there is a control to
 * change it. With the time bar hidden, a stored `landsat` + an old date would
 * leave the "Sentinel-2" base map quietly serving 30 m Landsat from years back,
 * with no way to get out of it. So when the picker is unavailable those two
 * fields fall back to their defaults: Sentinel-2, newest clear scene. Everything
 * else (style, cloud, range) is still restored.
 */
export function loadImageryPrefs(pickerAvailable = true) {
  const base = (() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return { ...DEFAULT_PREFS };
      return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
    } catch {
      return { ...DEFAULT_PREFS };
    }
  })();

  if (!pickerAvailable && (base.sensor !== DEFAULT_PREFS.sensor || base.date)) {
    return {
      ...base,
      sensor: DEFAULT_PREFS.sensor,
      date: DEFAULT_PREFS.date,
      // The remembered style belongs to whichever sensor was selected, and the
      // two do not share style ids, so it has to reset with the sensor.
      styleId: DEFAULT_PREFS.styleId,
    };
  }
  return base;
}

export function saveImageryPrefs(prefs) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Private browsing / storage disabled — the map still works, it just
    // forgets the date between visits. Never let this break rendering.
  }
}

/** Human age of an acquisition, for the age chip and the time bar. */
export function describeAge(isoDatetime) {
  if (!isoDatetime) return null;
  const then = new Date(isoDatetime).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
  let text;
  if (days === 0) text = 'today';
  else if (days === 1) text = '1 day old';
  else if (days < 45) text = `${days} days old`;
  else if (days < 365) text = `${Math.round(days / 30)} months old`;
  else {
    const years = days / 365;
    text = years < 10 ? `${years.toFixed(1)} years old` : `${Math.round(years)} years old`;
  }
  // Bands drive the chip colour: fresh enough to act on, usable, or historic.
  const band = days <= 30 ? 'fresh' : days <= 730 ? 'recent' : 'old';
  return { days, text, band };
}
